import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import copy from "../../public/locales/en/diagnostics.json";
import { useAppDispatch, useAppSelector } from "../store";
import {
  executeDiagnostic,
  fetchDiagnostics,
} from "../store/slices/diagnosticsSlice";

import type {
  DiagnosticCheck,
  DiagnosticResult,
} from "../store/slices/diagnosticsSlice";
import type { ReactElement } from "react";

const groupOrder = [
  "configuration",
  "infrastructure",
  "authentication",
  "payments",
  "ai",
  "email",
];

const checkLabel = (id: string): string =>
  (copy.checks as Record<string, string>)[id] ?? id;
const groupLabel = (group: string): string =>
  (copy.groups as Record<string, string>)[group] ?? group;

const ResultDetails = ({
  result,
}: {
  result: DiagnosticResult;
}): ReactElement => (
  <Stack spacing={1} sx={{ mt: 2 }}>
    <Alert
      severity={
        result.status === "passed"
          ? "success"
          : result.status === "warning"
            ? "warning"
            : "error"
      }
    >
      {result.message}
    </Alert>
    {result.details &&
      Object.entries(result.details).map(([key, value]) => (
        <Typography key={key} variant="body2" sx={{ overflowWrap: "anywhere" }}>
          <strong>{key}:</strong> {value}
        </Typography>
      ))}
    {result.audioBase64 && (
      <audio controls src={`data:audio/mpeg;base64,${result.audioBase64}`} />
    )}
    <Typography variant="caption" color="text.secondary">
      {copy.checkedAt}: {new Date(result.checkedAt).toLocaleString()} ·{" "}
      {copy.duration}: {result.durationMs} ms
    </Typography>
  </Stack>
);

const DiagnosticsPage = (): ReactElement => {
  const dispatch = useAppDispatch();
  const { checks, results, running, loading, error } = useAppSelector(
    (state) => state.diagnostics,
  );
  const [recipient, setRecipient] = useState("");
  const validRecipient = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient);

  useEffect((): void => {
    void dispatch(fetchDiagnostics());
  }, [dispatch]);

  const runCheck = (check: DiagnosticCheck): void => {
    void dispatch(
      executeDiagnostic({
        id: check.id,
        recipient: check.group === "email" ? recipient.trim() : undefined,
      }),
    );
  };

  const runReadChecks = async (): Promise<void> => {
    for (const check of checks.filter((item) => item.kind === "read")) {
      await dispatch(executeDiagnostic({ id: check.id }));
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {copy.title}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {copy.intro}
      </Typography>
      <Button
        variant="contained"
        onClick={() => {
          void runReadChecks();
        }}
        disabled={loading || Object.values(running).some(Boolean)}
        sx={{ mb: 3 }}
      >
        {copy.runAllRead}
      </Button>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <CircularProgress />}
      {checks.length > 0 && (
        <TextField
          fullWidth
          label={copy.recipient}
          helperText={copy.recipientHint}
          value={recipient}
          onChange={(event) => setRecipient(event.target.value)}
          type="email"
          sx={{ mb: 3 }}
        />
      )}
      {groupOrder.map((group) => {
        const items = checks.filter((check) => check.group === group);
        if (!items.length) return null;
        return (
          <Box key={group} sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {groupLabel(group)}
            </Typography>
            <Grid container spacing={2}>
              {items.map((check) => {
                const result = results[check.id];
                const isRunning = !!running[check.id];
                const disabled =
                  isRunning || (group === "email" && !validRecipient);
                return (
                  <Grid key={check.id} size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography variant="h6">
                            {checkLabel(check.id)}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              isRunning
                                ? copy.running
                                : result
                                  ? copy[result.status]
                                  : copy.pending
                            }
                            color={
                              result?.status === "passed"
                                ? "success"
                                : result?.status === "failed"
                                  ? "error"
                                  : result?.status === "warning"
                                    ? "warning"
                                    : "default"
                            }
                          />
                        </Stack>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => runCheck(check)}
                          disabled={disabled}
                          sx={{ mt: 2 }}
                          startIcon={
                            isRunning ? <CircularProgress size={14} /> : null
                          }
                        >
                          {copy.run}
                        </Button>
                        {result && <ResultDetails result={result} />}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

export default DiagnosticsPage;
